import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

interface BaseProps {
  label: string;
  error?: string;
  required?: boolean;
}

type InputProps = BaseProps & InputHTMLAttributes<HTMLInputElement> & { as?: 'input' };
type SelectProps = BaseProps & SelectHTMLAttributes<HTMLSelectElement> & { as: 'select'; children: React.ReactNode };
type TextareaProps = BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement> & { as: 'textarea' };

type FormFieldProps = InputProps | SelectProps | TextareaProps;

const baseStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-3) var(--space-4)',
  fontSize: 'var(--text-sm)',
  fontFamily: 'var(--font-sans)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  transition: 'border-color var(--duration-fast), box-shadow var(--duration-fast)',
  outline: 'none',
};

export function FormField(props: FormFieldProps) {
  const { label, error, required, as = 'input', ...rest } = props;
  const id = rest.id || label.toLowerCase().replace(/\s+/g, '-');
  const errorStyle = error ? { borderColor: 'var(--color-error)' } : {};

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 'var(--text-sm)',
          fontWeight: 500,
          marginBottom: 'var(--space-2)',
          color: 'var(--color-text-primary)',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--color-error)', marginLeft: '2px' }}>*</span>}
      </label>

      {as === 'select' ? (
        <select
          id={id}
          aria-required={required}
          aria-invalid={!!error}
          style={{ ...baseStyle, ...errorStyle }}
          {...(rest as SelectHTMLAttributes<HTMLSelectElement>)}
        >
          {(props as SelectProps).children}
        </select>
      ) : as === 'textarea' ? (
        <textarea
          id={id}
          aria-required={required}
          aria-invalid={!!error}
          rows={3}
          style={{ ...baseStyle, ...errorStyle, resize: 'vertical' }}
          {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          id={id}
          aria-required={required}
          aria-invalid={!!error}
          style={{ ...baseStyle, ...errorStyle }}
          {...(rest as InputHTMLAttributes<HTMLInputElement>)}
        />
      )}

      {error && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)', marginTop: 'var(--space-1)' }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
